import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { useSupabaseAuth } from '@/contexts/SupabaseAuthContext';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import WhatsAppButton from '@/components/WhatsAppButton';
import SignupDialog from '@/components/SignupDialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { ShoppingCart, CheckCircle, Shield, Zap, BookOpen } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { trackInitiateCheckout, trackAddPaymentInfo, trackPurchase, PRODUCT_DATA } from '@/lib/metaPixel';

declare global {
  interface Window {
    Cashfree: any;
  }
}

const Checkout: React.FC = () => {
  const { user, isLoading } = useSupabaseAuth();
  const navigate = useNavigate();
  const [isProcessing, setIsProcessing] = useState(false);
  const [showSignupDialog, setShowSignupDialog] = useState(false);

  useEffect(() => {
    // Meta Pixel - InitiateCheckout
    trackInitiateCheckout({
      content_name: 'Checkout Page - Class 10 Science Notes',
      content_ids: PRODUCT_DATA.content_ids,
      content_type: PRODUCT_DATA.content_type,
      value: PRODUCT_DATA.value,
      currency: PRODUCT_DATA.currency,
      num_items: 1,
    });
  }, []);

  const handleBuyNow = async () => {
    if (!user) {
      setShowSignupDialog(true);
      return;
    }

    setIsProcessing(true);
    try {
      const { data, error } = await supabase.functions.invoke('create-cashfree-order', {
        body: { userId: user.id }
      });

      if (error) throw error;

      if (data?.payment_session_id) {
        const cashfree = window.Cashfree({ mode: 'production' });
        await cashfree.checkout({
          paymentSessionId: data.payment_session_id,
          redirectTarget: '_self',
        });
      }
    } catch (err: any) {
      console.error('Payment error:', err);
      toast.error('Payment failed. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Checkout - Buy Toppers Notes at ₹149 | TOPPERS BUDDY</title>
        <meta name="description" content="Complete your purchase of class 10 science notes. Secure checkout with UPI, Card, Net Banking. Get instant access to toppers short notes after payment." />
        <meta name="keywords" content="checkout toppers notes, buy class 10 notes, secure payment, toppers buddy checkout" />
        <link rel="canonical" href="https://toppersbuddy.lovable.app/checkout" />
        <meta property="og:title" content="Checkout - Buy Toppers Notes at ₹149" />
        <meta property="og:url" content="https://toppersbuddy.lovable.app/checkout" />
      </Helmet>

      <div className="flex min-h-screen flex-col bg-background">
        <Header />
        <main role="main" aria-label="Checkout" className="flex-1 py-12">
          <div className="container max-w-2xl mx-auto px-4">
            <Card className="border-border/50 shadow-xl">
              <CardHeader className="text-center border-b pb-6">
                <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
                  <ShoppingCart className="h-8 w-8 text-primary" />
                </div>
                <CardTitle className="text-2xl font-bold">Checkout</CardTitle>
                <CardDescription>Complete your purchase to get instant access</CardDescription>
              </CardHeader>
              <CardContent className="pt-6">
                {/* Product Summary */}
                <div className="mb-6 p-4 bg-muted/50 rounded-lg">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <BookOpen className="h-10 w-10 text-primary" />
                      <div>
                        <h3 className="font-semibold">Class 10 Science Notes</h3>
                        <p className="text-sm text-muted-foreground">Physics + Chemistry + Biology</p>
                        <Badge variant="secondary" className="mt-1">2025-2026 Edition</Badge>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm line-through text-muted-foreground">₹499</p>
                      <p className="text-2xl font-bold text-primary">₹149</p>
                      <Badge className="bg-green-500">70% OFF</Badge>
                    </div>
                  </div>
                </div>

                {/* Features */}
                <div className="mb-6 space-y-3">
                  <div className="flex items-center gap-2 text-sm">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <span>Complete Physics, Chemistry, Biology notes</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <span>All diagrams and formulas included</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Zap className="h-4 w-4 text-yellow-500" />
                    <span>Instant access after payment</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Shield className="h-4 w-4 text-blue-500" />
                    <span>Secure payment via Cashfree</span>
                  </div>
                </div>

                {/* Price Summary */}
                <div className="mb-6 p-4 border rounded-lg space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Subtotal</span>
                    <span>₹499</span>
                  </div>
                  <div className="flex justify-between text-sm text-green-600">
                    <span>Discount (70%)</span>
                    <span>-₹350</span>
                  </div>
                  <div className="border-t pt-2 flex justify-between font-bold text-lg">
                    <span>Total</span>
                    <span className="text-primary">₹149</span>
                  </div>
                </div>

                {/* CTA Button */}
                <Button 
                  onClick={handleBuyNow} 
                  className="w-full h-14 text-lg"
                  disabled={isProcessing}
                >
                  {isProcessing ? 'Processing...' : 'Pay ₹149 Now'}
                </Button>

                <p className="mt-4 text-center text-xs text-muted-foreground">
                  By proceeding, you agree to our Terms & Conditions
                </p>
              </CardContent>
            </Card>
          </div>
        </main>
        <Footer />
      </div>
      
      <WhatsAppButton />
      <SignupDialog open={showSignupDialog} onOpenChange={setShowSignupDialog} onComplete={() => setShowSignupDialog(false)} />
    </>
  );
};

export default Checkout;
